import { calculateHexColorBrightness } from "helpers/Helpers";
import { CSSProperties } from "react";
import { IProduct } from "redux/reducers/models/product";

export interface IFlyToCartRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface IFlyToCartProductVisual {
  title: string;
  backgroundStyle: CSSProperties;
  titleStyle: CSSProperties;
}

export interface IFlyToCartRects {
  sourceRect: IFlyToCartRect;
  targetRect: IFlyToCartRect;
}

const FLY_DURATION_MS = 650;
const FLY_EASING = "cubic-bezier(0.22, 1, 0.36, 1)";

export function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function getRectFromElement(element: HTMLElement | null): IFlyToCartRect | null {
  if (!element) {
    return null;
  }
  const rect = element.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) {
    return null;
  }
  return {
    left: rect.left,
    top: rect.top,
    width: rect.width,
    height: rect.height,
  };
}

export function buildFlyToCartProductVisual(product: IProduct, showProductImages?: boolean): IFlyToCartProductVisual {
  const title = product?.preferred_title ? product.preferred_title : product?.title ?? "";
  const hasImage = Boolean(product?.default_image?.source && showProductImages);
  const backgroundColorBrightness = product?.meta?.button_color
    ? calculateHexColorBrightness(product.meta.button_color)
    : null;
  const foregroundColor =
    backgroundColorBrightness === null ? "white" : backgroundColorBrightness < 50 ? "white" : "black";

  const textBgStyle: CSSProperties = hasImage
    ? { backgroundColor: "rgba(0, 0, 0, 0.8)", borderRadius: "4px", padding: "2px 4px" }
    : { color: foregroundColor };

  const backgroundStyle: CSSProperties = hasImage
    ? {
        backgroundImage: `url(${product.default_image.source})`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
      }
    : product?.meta?.button_color
    ? { background: product.meta.button_color, color: foregroundColor }
    : {};

  return {
    title,
    backgroundStyle,
    titleStyle: textBgStyle,
  };
}

export function getFlyCloneStyle(rect: IFlyToCartRect, product: IFlyToCartProductVisual): CSSProperties {
  return {
    ...product.backgroundStyle,
    left: rect.left,
    top: rect.top,
    width: rect.width,
    height: rect.height,
  };
}

export function runFlyToCartAnimation(
  element: HTMLElement,
  rects: IFlyToCartRects,
  onComplete: () => void,
): () => void {
  let completed = false;
  const finish = () => {
    if (completed) {
      return;
    }
    completed = true;
    onComplete();
  };

  if (prefersReducedMotion()) {
    finish();
    return () => {};
  }

  const { sourceRect, targetRect } = rects;
  const sourceCenterX = sourceRect.left + sourceRect.width / 2;
  const sourceCenterY = sourceRect.top + sourceRect.height / 2;
  const targetCenterX = targetRect.left + targetRect.width / 2;
  const targetCenterY = targetRect.top + targetRect.height / 2;
  const translateX = targetCenterX - sourceCenterX;
  const translateY = targetCenterY - sourceCenterY;

  element.style.left = `${sourceRect.left}px`;
  element.style.top = `${sourceRect.top}px`;
  element.style.width = `${sourceRect.width}px`;
  element.style.height = `${sourceRect.height}px`;
  element.style.transform = "translate(0, 0) scale(1)";
  element.style.opacity = "1";

  const animation = element.animate(
    [
      { transform: "translate(0, 0) scale(1)", opacity: 1 },
      { transform: `translate(${translateX}px, ${translateY}px) scale(0.3)`, opacity: 0.5 },
    ],
    {
      duration: FLY_DURATION_MS,
      easing: FLY_EASING,
      fill: "forwards",
    },
  );

  animation.onfinish = finish;

  return () => {
    animation.onfinish = null;
    if (!completed) {
      animation.cancel();
    }
  };
}
